// src/pages/Team.jsx — Team management for Business plan
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Modal, ModalFooter } from '../components/ui/Modal';
import {
  Users, UserPlus, Mail, Trash2, RefreshCw, Crown,
  ShieldCheck, User, Clock, CheckCircle, XCircle, Copy,
  Lock, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonTeam } from '../components/ui/Skeleton';
import { Link } from 'react-router-dom';

const ROLE_BADGE = { admin: 'default', member: 'secondary' };

export default function Team() {
  const { user, profile, tier } = useAuth();
  const isBusiness = tier === 'business' || profile?.role === 'admin' || profile?.role === 'superadmin';
  const isTeamMember = !!profile?.is_team_member;

  const [teamData, setTeamData]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole]   = useState('member');
  const [inviting, setInviting]       = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [targetMember, setTargetMember]       = useState(null);
  const [removing, setRemoving]               = useState(false);
  const [lastInviteLink, setLastInviteLink]   = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res  = await fetch('/api/team', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getTeam', userId: user.id }) });
      const data = await res.json();
      if (data.success) setTeamData(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) { toast.error('Enter an email address'); return; }
    setInviting(true);
    const tid = toast.loading(`Sending invite to ${inviteEmail}...`);
    try {
      const res  = await fetch('/api/team', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'invite', userId: user.id, email: inviteEmail.trim(), role: inviteRole }) });
      const data = await res.json();
      if (data.success) {
        toast.success(`Invite sent to ${inviteEmail}`, { id: tid });
        setLastInviteLink(data.inviteLink);
        setInviteEmail('');
        load();
      } else {
        toast.error(data.error || 'Invite failed', { id: tid });
      }
    } catch (e) { toast.error(e.message, { id: tid }); }
    setInviting(false);
  };

  const handleRevoke = async (invitationId) => {
    await fetch('/api/team', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'revokeInvite', userId: user.id, invitationId }) });
    toast.success('Invite revoked');
    load();
  };

  const handleRemove = async () => {
    if (!targetMember) return;
    setRemoving(true);
    await fetch('/api/team', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'removeMember', userId: user.id, memberId: targetMember.profiles?.id }) });
    toast.success('Member removed');
    setShowRemoveModal(false); setTargetMember(null); setRemoving(false);
    load();
  };

  const handleRoleChange = async (memberId, role) => {
    await fetch('/api/team', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'updateRole', userId: user.id, memberId, role }) });
    toast.success('Role updated');
    load();
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => toast.success('Invite link copied'));
  };

  // ── Team member view (read-only) ──────────────────────────────────────────
  if (isTeamMember) {
    return (
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6" /> Team</h1>
          <p className="text-muted-foreground text-sm">You're a member of this workspace.</p>
        </div>
        <Card>
          <CardContent className="p-8 text-center">
            <ShieldCheck className="h-12 w-12 mx-auto text-primary mb-3" />
            <p className="font-semibold">You have team member access</p>
            <p className="text-sm text-muted-foreground mt-1">Team management is available to the account owner.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Upgrade prompt ────────────────────────────────────────────────────────
  if (!isBusiness) {
    return (
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6" /> Team</h1>
          <p className="text-muted-foreground text-sm">Invite team members to collaborate in RevHapi.</p>
        </div>
        <Card className="border-2 border-primary/30">
          <CardContent className="p-8 text-center">
            <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-bold mb-2">Business plan required</h2>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
              Invite up to 2 team members, manage 3 Shopify stores, and collaborate — all on the Business plan at $69/month.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 text-sm">
              {[
                { icon: '🛍️', label: '3 Shopify stores' },
                { icon: '👥', label: '2 team members' },
                { icon: '💳', label: 'Centralised billing' },
              ].map(f => (
                <div key={f.label} className="flex items-center justify-center gap-2 p-3 bg-accent rounded-xl">
                  <span>{f.icon}</span><span className="font-medium">{f.label}</span>
                </div>
              ))}
            </div>
            <Link to="/subscribe">
              <Button size="lg"><Crown className="h-4 w-4 mr-2" /> Upgrade to Business <ArrowRight className="h-4 w-4 ml-2" /></Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const maxMembers  = teamData?.maxMembers || 2;
  const members     = teamData?.members    || [];
  const invitations = teamData?.invitations || [];
  const slotsUsed   = members.length + invitations.length;
  const slotsLeft   = Math.max(0, maxMembers - slotsUsed);

  if (loading) return <SkeletonTeam />;

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6" /> Team</h1>
          <p className="text-muted-foreground text-sm">{slotsUsed} of {maxMembers} member slots used</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Owner card */}
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Account owner</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-semibold text-primary">
              {profile?.username?.[0]?.toUpperCase() || 'Y'}
            </div>
            <div>
              <p className="font-medium">{profile?.username}</p>
              <p className="text-sm text-muted-foreground">{profile?.email}</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Badge><Crown className="h-3 w-3 mr-1" />Owner</Badge>
              <span className="text-xs text-muted-foreground">Billing & team access</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active members */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Team members</CardTitle>
              <CardDescription>{members.length} active member{members.length !== 1 ? 's' : ''}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No team members yet. Invite someone below.
            </div>
          ) : (
            <div className="divide-y">
              {members.map(m => (
                <div key={m.id} className="flex items-center gap-3 py-3">
                  <div className="h-9 w-9 rounded-full bg-accent flex items-center justify-center font-medium text-sm flex-shrink-0">
                    {m.profiles?.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{m.profiles?.username || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{m.profiles?.email}</p>
                  </div>
                  <select value={m.role} onChange={e => handleRoleChange(m.profiles?.id, e.target.value)}
                    className="text-xs border border-border rounded-lg px-2 py-1 bg-background">
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button onClick={() => { setTargetMember(m); setShowRemoveModal(true); }}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Pending invites</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y">
              {invitations.map(inv => (
                <div key={inv.id} className="flex items-center gap-3 py-3">
                  <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{inv.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Expires {new Date(inv.expires_at).toLocaleDateString()} · {inv.role}
                    </p>
                  </div>
                  <Badge variant="warning"><Clock className="h-3 w-3 mr-1" />Pending</Badge>
                  <button onClick={() => handleRevoke(inv.id)}
                    className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive">
                    <XCircle className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite form */}
      <Card>
        <CardHeader>
          <CardTitle>Invite a team member</CardTitle>
          <CardDescription>
            {slotsLeft > 0
              ? `${slotsLeft} slot${slotsLeft !== 1 ? 's' : ''} remaining`
              : 'All member slots are in use'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {slotsLeft === 0 ? (
            <div className="text-sm text-muted-foreground bg-accent rounded-xl p-4 text-center">
              You've used all {maxMembers} member slots. Remove a member or a pending invite to add someone new.
            </div>
          ) : (
            <>
              <div className="flex gap-3">
                <Input
                  placeholder="colleague@company.com"
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleInvite()}
                  className="flex-1"
                />
                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
                  className="border border-border rounded-xl px-3 text-sm bg-background">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
                  {inviting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                  Invite
                </Button>
              </div>

              <div className="text-xs text-muted-foreground bg-accent/50 rounded-lg p-3 space-y-1">
                <p><strong>Member:</strong> Can view all dashboards, fetch data, and manage ad accounts.</p>
                <p><strong>Admin:</strong> All member permissions plus team management (cannot manage billing).</p>
              </div>
            </>
          )}

          {lastInviteLink && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-green-800 mb-1">Invite sent! Share this link as a backup:</p>
              <div className="flex items-center gap-2">
                <code className="text-xs text-green-700 flex-1 truncate">{lastInviteLink}</code>
                <button onClick={() => copyLink(lastInviteLink)} className="flex-shrink-0 text-green-700 hover:text-green-900">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions info */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Access permissions</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            {[
              { label: 'Owner', features: ['Billing & subscription', 'Add/remove team members', 'All platform settings', 'All dashboards & reports'] },
              { label: 'Admin', features: ['All dashboards & reports', 'Manage ad accounts', 'Manage team members', 'Cannot access billing'] },
              { label: 'Member', features: ['All dashboards & reports', 'Fetch & view data', 'Manage ad accounts', 'No team or billing access'] },
            ].map(col => (
              <div key={col.label} className="space-y-2">
                <p className="font-semibold">{col.label}</p>
                <ul className="space-y-1">
                  {col.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />{f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Remove confirm modal */}
      <Modal isOpen={showRemoveModal} onClose={() => { setShowRemoveModal(false); setTargetMember(null); }}
        title="Remove team member"
        description={`Remove ${targetMember?.profiles?.username || targetMember?.profiles?.email} from your team? They will lose access to RevHapi.`}>
        <ModalFooter>
          <Button variant="outline" onClick={() => { setShowRemoveModal(false); setTargetMember(null); }}>Cancel</Button>
          <Button variant="destructive" onClick={handleRemove} disabled={removing}>
            {removing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null} Remove member
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
